// generateVcatPdf.js
// Client-side VCAT evidence PDF generator using jsPDF + jspdf-autotable
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Australia/Melbourne',
  });
}

function fmtMoney(n) {
  if (n === null || n === undefined) return '—';
  return '$' + Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function todayAU() {
  return new Date().toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Melbourne',
  });
}

function noticesInLast12Months(notices) {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  return notices.filter(n => new Date(n.issued_date) >= cutoff);
}

const NOTICE_TYPE_LABELS = {
  notice_to_vacate: 'Notice to Vacate',
  reminder: 'Payment Reminder',
  other: 'Other',
};

const DELIVERY_LABELS = {
  email: 'Email',
  post: 'Post',
  hand_delivered: 'Hand Delivered',
};

// ── Colours ───────────────────────────────────────────────────────────────────
const C = {
  brand:     [37, 99, 235],   // #2563EB blue
  purple:    [124, 58, 237],  // #7C3AED
  dark:      [30, 41, 59],    // #1E293B slate-800
  mid:       [100, 116, 139], // #64748B slate-500
  light:     [241, 245, 249], // #F1F5F9 slate-100
  white:     [255, 255, 255],
  red:       [220, 38, 38],
  amber:     [217, 119, 6],
  green:     [22, 163, 74],
  rowRed:    [255, 240, 240],
  rowAmber:  [255, 251, 235],
  rowBlue:   [239, 246, 255],
};

// ── Main export ───────────────────────────────────────────────────────────────

export function generateVcatPdf({ property, tenancy, ledger, periods, notices, reports, landlordName, preparedBy, consentStatus = null, documents = [] }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = W - margin * 2;
  const today = todayAU();
  const totalPages = () => doc.internal.getNumberOfPages();

  // ── Footer helper ─────────────────────────────────────────────────────────
  function addFooter(pageNum) {
    const y = H - 10;
    doc.setFontSize(7);
    doc.setTextColor(...C.mid);
    doc.text(`RentFriendly — rentfriendly.com.au`, margin, y);
    doc.text(`Rent Payment Record — ${property.address}`, W / 2, y, { align: 'center' });
    doc.text(`Page ${pageNum} of {TOTAL}`, W - margin, y, { align: 'right' });
    doc.setFontSize(6);
    doc.text(
      'This document was generated by RentFriendly. The accuracy of this record depends on information entered by the rental provider. This is not legal advice — seek independent legal advice for VCAT proceedings.',
      margin, H - 6, { maxWidth: contentW }
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // PAGE 1 — COVER
  // ══════════════════════════════════════════════════════════════════════════
  let y = margin;

  // Logo
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.brand);
  doc.text('RentFriendly', margin, y);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('rentfriendly.com.au', margin, y + 6);

  // Main heading
  y += 22;
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('RENT PAYMENT RECORD', W / 2, y, { align: 'center' });
  y += 8;
  doc.setFontSize(13);
  doc.setTextColor(...C.purple);
  doc.text('Evidence for VCAT Proceedings', W / 2, y, { align: 'center' });
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('Victorian Civil and Administrative Tribunal', W / 2, y, { align: 'center' });

  // HR
  y += 7;
  doc.setDrawColor(...C.dark);
  doc.setLineWidth(0.5);
  doc.line(margin, y, W - margin, y);

  // Two-column details
  y += 8;
  const col2 = W / 2 + 2;
  doc.setFontSize(9);

  function detailRow(label, value, x, yPos) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(label + ':', x, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.text(String(value || '—'), x + 42, yPos);
    return yPos + 5.5;
  }

  let yL = y;
  let yR = y;
  yL = detailRow('Property address', property.address, margin, yL);
  yL = detailRow('Tenant name', tenancy.tenant_name, margin, yL);
  yL = detailRow('Tenant email', tenancy.tenant_email, margin, yL);
  yL = detailRow('Lease commenced', fmtDate(tenancy.lease_start_date), margin, yL);
  yL = detailRow('Fortnightly rent', fmtMoney(tenancy.fortnightly_rent), margin, yL);

  yR = detailRow('Rental provider', landlordName, col2, yR);
  yR = detailRow('Report generated', fmtDateTime(new Date().toISOString()), col2, yR);
  yR = detailRow('Prepared by', preparedBy, col2, yR);
  yR = detailRow('Total periods', String(periods.length), col2, yR);
  yR = detailRow('Report covers', `${fmtDate(tenancy.lease_start_date)} to ${today}`, col2, yR);

  y = Math.max(yL, yR) + 6;

  // Arrears summary box
  doc.setFillColor(...C.light);
  doc.roundedRect(margin, y, contentW, 50, 2, 2, 'F');
  y += 7;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text(`ARREARS SUMMARY AS OF ${today.toUpperCase()}`, margin + 5, y);
  y += 7;
  doc.setFontSize(9);

  function summaryRow(label, value, color) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.text(label, margin + 5, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(color || C.dark));
    doc.text(String(value), margin + 60, y);
    y += 6;
  }

  summaryRow('Paid to date:', fmtDate(ledger.paidToDate));
  summaryRow('Days overdue:', ledger.inArrears ? `${ledger.arrearsDays} days` : 'Current', ledger.inArrears ? C.red : C.green);
  summaryRow('Amount outstanding:', fmtMoney(ledger.arrearsAmount || 0), ledger.inArrears ? C.red : C.green);

  const last12 = noticesInLast12Months(notices);
  summaryRow('Notices issued:', `${notices.length} total (${last12.length} in last 12 months)`);

  // Consent status row
  const consentLabel = consentStatus?.consentActive
    ? `Given (${consentStatus.consentMethod === 'invite_flow' ? 'tenant portal' : 'landlord recorded'}) on ${fmtDate(consentStatus.consentDate?.split('T')[0])}`
    : consentStatus?.consentWithdrawn
      ? 'Withdrawn by tenant'
      : 'Not on file';
  summaryRow('Electronic consent:', consentLabel,
    consentStatus?.consentActive ? C.green : C.amber);

  addFooter(1);

  // ══════════════════════════════════════════════════════════════════════════
  // PAGE 2 — PAYMENT HISTORY TABLE
  // ══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  y = margin;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('RENT PAYMENT HISTORY', margin, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('All fortnightly periods from lease commencement', margin, y);
  y += 6;

  // Build table rows
  let totalDue = 0;
  let totalPaid = 0;

  const tableRows = periods.map((p, i) => {
    const periodLabel = `${fmtDate(p.dueDate)} – ${fmtDate(p.periodEnd)}`;
    const payDates = p.paymentsInPeriod && p.paymentsInPeriod.length
      ? p.paymentsInPeriod.map(pay => fmtDate(pay.received_date)).join(' + ')
      : '—';
    const paidAmt = p.periodPaid > 0 ? fmtMoney(p.periodPaid) : '—';

    let daysLate = '0';
    if (p.status === 'OVERDUE') daysLate = 'UNPAID';
    else if (p.paymentsInPeriod && p.paymentsInPeriod.length) {
      const lastPay = p.paymentsInPeriod[p.paymentsInPeriod.length - 1];
      const due = new Date(p.dueDate);
      const paid = new Date(lastPay.received_date);
      const diff = Math.floor((paid - due) / 86400000);
      daysLate = diff > 0 ? `${diff} days` : '0';
    }

    totalDue += p.amountDue || 0;
    totalPaid += p.periodPaid || 0;

    return [
      String(i + 1),
      periodLabel,
      fmtDate(p.dueDate),
      fmtMoney(p.amountDue),
      payDates,
      paidAmt,
      daysLate,
      p.status,
    ];
  });

  // Totals row
  tableRows.push([
    '', 'TOTALS', '', fmtMoney(totalDue), '', fmtMoney(totalPaid), '', '',
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'Period', 'Due Date', 'Amount Due', 'Date(s) Paid', 'Amount Paid', 'Days Late', 'Status']],
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: C.dark, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 38 },
      2: { cellWidth: 22 },
      3: { cellWidth: 22 },
      4: { cellWidth: 28 },
      5: { cellWidth: 22 },
      6: { cellWidth: 16 },
      7: { cellWidth: 14 },
    },
    didParseCell(data) {
      const row = data.row.raw;
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = C.light;
        return;
      }
      const status = row[7];
      if (status === 'OVERDUE') data.cell.styles.fillColor = C.rowRed;
      else if (status === 'PARTIAL') data.cell.styles.fillColor = C.rowAmber;
      else if (status === 'AHEAD') data.cell.styles.fillColor = C.rowBlue;
    },
    didDrawCell(data) {
      if (data.column.index === 7 && data.section === 'body' && data.row.index < tableRows.length - 1) {
        const status = data.row.raw[7];
        const colours = { PAID: C.green, OVERDUE: C.red, PARTIAL: C.amber, AHEAD: C.brand };
        const col = colours[status] || C.dark;
        doc.setTextColor(...col);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1.5, { align: 'center' });
        // suppress default text
        doc.setTextColor(...C.dark);
      }
    },
    didDrawPage(data) {
      addFooter(data.pageNumber + 1);
    },
  });

  // ══════════════════════════════════════════════════════════════════════════
  // PAGE 3 — NOTICES (only if notices exist)
  // ══════════════════════════════════════════════════════════════════════════
  if (notices.length > 0) {
    doc.addPage();
    y = margin;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text('NOTICES ISSUED TO TENANT', margin, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.text('Under the Residential Tenancies Act 1997 (VIC)', margin, y);
    y += 7;

    // Strike counter box
    const n12 = last12.length;
    const boxColor = n12 >= 5 ? C.rowRed : n12 >= 4 ? C.rowAmber : C.light;
    doc.setFillColor(...boxColor);
    doc.roundedRect(margin, y, contentW, 16, 2, 2, 'F');
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(`NOTICES IN LAST 12 MONTHS: ${n12} of 4 permitted`, margin + 4, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (n12 >= 5) {
      doc.setTextColor(...C.red);
      doc.text('TERMINATION ORDER GROUNDS MAY EXIST — seek legal advice or contact Consumer Affairs VIC', margin + 4, y);
    } else if (n12 >= 4) {
      doc.setTextColor(...[180, 100, 0]);
      doc.text('⚠ THRESHOLD REACHED — further notices may support a termination order application', margin + 4, y);
    }
    y += 8;

    const noticeRows = notices.map((n, i) => {
      let outcome = 'Pending';
      if (n.vacate_date) {
        const vd = new Date(n.vacate_date);
        const now = new Date();
        if (vd < now) {
          outcome = n.tenant_paid_by_vacate === 1 ? 'Tenant paid ✓' : n.tenant_paid_by_vacate === 0 ? 'Tenant did not pay ✗' : 'Outcome not recorded';
        }
      }
      return [
        String(i + 1),
        fmtDate(n.issued_date),
        NOTICE_TYPE_LABELS[n.notice_type] || n.notice_type,
        DELIVERY_LABELS[n.delivery_method] || n.delivery_method,
        n.vacate_date ? fmtDate(n.vacate_date) : '—',
        outcome,
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date Issued', 'Notice Type', 'Delivered By', 'Vacate Date', 'Outcome']],
      body: noticeRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: C.dark, textColor: C.white, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 24 },
        2: { cellWidth: 38 },
        3: { cellWidth: 28 },
        4: { cellWidth: 24 },
        5: { cellWidth: 40 },
      },
      didDrawPage(data) {
        addFooter(data.pageNumber + 1);
      },
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── Supporting Documents section ──────────────────────────────────────────
  if (documents.length > 0) {
    doc.addPage();
    addFooter(doc.internal.getNumberOfPages());
    y = margin + 5;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text('SUPPORTING DOCUMENTS', margin, y);
    y += 3;
    doc.setDrawColor(...C.mid);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 1, W - margin, y + 1);
    y += 9;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.text('The following documents have been uploaded to RentFriendly in connection with this tenancy:', margin, y, { maxWidth: contentW });
    y += 9;

    const DOC_LABELS = {
      pre_inspection_report: 'Pre-Inspection Condition Report',
      inspection_image:      'Inspection Photograph',
      bond_receipt:          'Bond Receipt',
      bond_lodgement:        'Bond Lodgement Confirmation',
      lease_agreement:       'Lease Agreement',
      other:                 'Other Document',
    };

    // Table header row
    doc.setFillColor(...C.light);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.mid);
    doc.text('Document Type', margin + 3, y + 5);
    doc.text('Filename', margin + 75, y + 5);
    doc.text('Date Uploaded', W - margin - 3, y + 5, { align: 'right' });
    y += 9;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.dark);

    for (const docItem of documents) {
      if (y > H - 30) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        y = margin + 10;
      }
      const typeLabel = DOC_LABELS[docItem.document_type] || docItem.document_type;
      const uploaded  = docItem.uploaded_at
        ? new Date(docItem.uploaded_at).toLocaleDateString('en-AU', { day:'2-digit', month:'short', year:'numeric' })
        : '—';
      const fname = docItem.file_name.length > 40 ? docItem.file_name.slice(0, 37) + '…' : docItem.file_name;

      doc.setFontSize(8.5);
      doc.text(typeLabel, margin + 3, y + 4);
      doc.text(fname, margin + 75, y + 4);
      doc.text(uploaded, W - margin - 3, y + 4, { align: 'right' });
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.2);
      doc.line(margin, y + 7, W - margin, y + 7);
      y += 9;
    }

    y += 5;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...C.mid);

    const photoCount = documents.filter(d => d.document_type === 'inspection_image').length;
    if (photoCount > 0) {
      doc.text(`Pre-inspection photos: ${photoCount} image${photoCount !== 1 ? 's' : ''} uploaded — available on request.`, margin, y, { maxWidth: contentW });
      y += 6;
    }
    const bondDocs = documents.filter(d => d.document_type === 'bond_receipt' || d.document_type === 'bond_lodgement');
    if (bondDocs.length > 0) {
      doc.text('Bond lodgement: documented.', margin, y, { maxWidth: contentW });
      y += 6;
    }
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.text('Original documents available from the rental provider via RentFriendly.', margin, y, { maxWidth: contentW });
  }

  // FINAL PAGE — DECLARATION
  // ══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  y = margin;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('LANDLORD DECLARATION', margin, y);
  y += 3;
  doc.setDrawColor(...C.dark);
  doc.setLineWidth(0.4);
  doc.line(margin, y + 3, W - margin, y + 3);
  y += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.dark);

  const declaration = [
    `I, ${preparedBy}, being the rental provider (landlord) of the property at ${property.address}, declare that:`,
    '',
    `1.  The rent payment records contained in this document have been recorded by me in RentFriendly as payments were received from the tenant.`,
    '',
    `2.  The information in this report is accurate and complete to the best of my knowledge.`,
    '',
    `3.  This report was generated on ${today} from RentFriendly, a rent management platform for Australian self-managing landlords.`,
    '',
    `4.  I understand this document may be submitted as evidence in proceedings before the Victorian Civil and Administrative Tribunal (VCAT).`,
  ];

  declaration.forEach(line => {
    if (!line) { y += 3; return; }
    const wrapped = doc.splitTextToSize(line, contentW - 4);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 5.5;
  });

  // ── Consent section ──────────────────────────────────────────────────────
  y += 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('ELECTRONIC COMMUNICATION CONSENT', margin, y);
  y += 3;
  doc.setDrawColor(...C.mid);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 1, W - margin, y + 1);
  y += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.dark);

  if (consentStatus?.consentActive) {
    doc.setTextColor(...C.green);
    doc.setFont('helvetica', 'bold');
    doc.text('Status: CONSENT ON FILE', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    y += 6;
    const method = consentStatus.consentMethod === 'invite_flow'
      ? 'Tenant portal (online)'
      : consentStatus.consentMethod === 'landlord_recorded'
        ? 'Landlord recorded'
        : consentStatus.consentMethod || '—';
    doc.text(`The tenant consented to electronic service of notices on ${fmtDate(consentStatus.consentDate?.split('T')[0] || consentStatus.consentDate)}.`, margin, y);
    y += 5.5;
    doc.text(`Method: ${method}`, margin, y);
    y += 5.5;
    if (consentStatus.consentIpMasked) {
      doc.text(`Recorded from IP: ${consentStatus.consentIpMasked}`, margin, y);
      y += 5.5;
    }
    doc.text(`This confirms that all notices sent to ${tenancy.tenant_email} were delivered with the tenant's consent under the Residential Tenancies Act 1997 (Vic).`, margin, y, { maxWidth: contentW });
    y += 12;
  } else if (consentStatus?.consentWithdrawn) {
    doc.setTextColor(...C.amber);
    doc.setFont('helvetica', 'bold');
    doc.text('Status: CONSENT WITHDRAWN', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    y += 6;
    doc.text(`The tenant previously consented but withdrew consent on ${fmtDate(consentStatus.consentWithdrawnDate?.split('T')[0])}.`, margin, y, { maxWidth: contentW });
    y += 6;
    doc.text('Formal notices issued after the withdrawal date must have been delivered by hand or post.', margin, y, { maxWidth: contentW });
    y += 12;
  } else {
    doc.setTextColor(...C.red);
    doc.setFont('helvetica', 'bold');
    doc.text('Status: NO CONSENT ON FILE', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    y += 6;
    doc.text('The rental provider has no record of the tenant consenting to electronic service of notices.', margin, y, { maxWidth: contentW });
    y += 6;
    doc.text('Any notices listed in this report as delivered by email may not comply with the Residential Tenancies Act 1997 (Vic).', margin, y, { maxWidth: contentW });
    y += 12;
  }

  y += 2;
  doc.setDrawColor(...C.mid);
  doc.setLineWidth(0.3);

  doc.text('Signature:', margin, y);
  doc.line(margin + 22, y, margin + 90, y);
  y += 10;
  doc.text(`Name: ${preparedBy}`, margin, y);
  y += 10;
  doc.text('Date:', margin, y);
  doc.line(margin + 14, y, margin + 82, y);

  addFooter(doc.internal.getNumberOfPages());

  // ── Fix up total page count ───────────────────────────────────────────────
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // overwrite the {TOTAL} placeholder (crude but reliable)
    doc.setFontSize(7);
    doc.setTextColor(...C.mid);
    const footerY = H - 10;
    // Re-draw page X of Y (overwrite placeholder)
    doc.setFillColor(...C.white);
    doc.rect(W - margin - 26, footerY - 4, 26, 6, 'F');
    doc.text(`Page ${i} of ${total}`, W - margin, footerY, { align: 'right' });
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const safeAddr = property.address.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-');
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`RentFriendly-VCAT-Report-${safeAddr}-${dateStr}.pdf`);
}
